<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
// Not part of vuetify/components (only the full VDatePicker is), so it is imported directly.
import { VDatePickerMonths } from 'vuetify/components/VDatePicker';
import { storeToRefs } from 'pinia';
import { useFilters } from '@/composables/useFilters';
import { usePermissions } from '@/composables/usePermissions';
import { useGroupFilterStore } from '@/stores/group-filter';
import { useServiceCategoryFilterStore } from '@/stores/service-category-filter';
import { useCareerFilterStore } from '@/stores/career-filter';
import ResponsiveTabs, { type ResponsiveTabItem } from '@/components/responsive-tabs.vue';
import GroupFilterSelect from '@/components/group-filter-select.vue';
import ServiceCategoryFilterSelect from '@/components/service-category-filter-select.vue';
import CareerFilterSelect from './career-filter-select.vue';
import MonthlySummariesShow from './monthly-summaries-show.vue';
import FinancialSummariesShow from './financial-summaries-show.vue';
import PatientMetricsTab from './patient-metrics-tab.vue';

function capitalize(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

// The selects self-hide when their feature flag is off or the catalog is trivial.
const { groupIds } = storeToRefs(useGroupFilterStore());
const { categoryIds, uncategorized } = storeToRefs(useServiceCategoryFilterStore());
const { careers: careerFilter } = storeToRefs(useCareerFilterStore());

const filters = reactive({
  startDateTimeFilter: 'today' as string | undefined,
  offset: 0,
  groupIdIn: groupIds,
  serviceCategoryIdIn: categoryIds,
  caregiverCareerIn: careerFilter,
  // Only serialize when on: createQueryParams would keep a literal `false`.
  uncategorized: computed(() => (uncategorized.value ? true : undefined)),
});

const currentDate = computed(() => {
  const today = new Date();

  // Day pinned to 1: setMonth would keep today's day-of-month, overflowing into the
  // next month whenever the target month is shorter (e.g. the 29th → February → March).
  return new Date(today.getFullYear(), today.getMonth() + filters.offset, 1);
});

// The picker's own state, seeded from the committed filter each time it opens: the year can
// be browsed without committing until a month is picked.
const monthPickerOpen = ref(false);
const pickerMonth = ref(currentDate.value.getMonth());
const pickerYear = ref(currentDate.value.getFullYear());

watch(monthPickerOpen, (open) => {
  if (!open) return;
  pickerMonth.value = currentDate.value.getMonth();
  pickerYear.value = currentDate.value.getFullYear();
});

const currentDateText = computed(() => {
  const monthName = currentDate.value.toLocaleDateString('es-CL', { month: 'long' });
  const year = currentDate.value.getFullYear();

  return `${capitalize(monthName)} ${year}`;
});

const queryParams = useFilters(filters);

function updateOffset(monthDelta: number) {
  filters.offset += monthDelta;
}

function resetOffset() {
  filters.offset = 0;
}

function selectMonth(month: number) {
  const selectedDate = new Date(pickerYear.value, month, 1);
  const today = new Date();

  const MONTHS_IN_YEAR = 12;
  const monthDiff = (selectedDate.getFullYear() - today.getFullYear()) * MONTHS_IN_YEAR +
                   (selectedDate.getMonth() - today.getMonth());

  filters.offset = monthDiff;
  monthPickerOpen.value = false;
}

const { hasPermission } = usePermissions();

const canReadFinancialInformation = computed(() =>
  hasPermission.value('shifts', 'read_financial_information'));

const tabItems = computed<ResponsiveTabItem[]>(() => {
  const items: ResponsiveTabItem[] = [
    { value: 'general', title: 'General', icon: 'mdi-view-dashboard' },
  ];
  if (canReadFinancialInformation.value) {
    items.push({ value: 'finances', title: 'Finanzas', icon: 'mdi-finance' });
  }
  items.push({ value: 'patients', title: 'Pacientes', icon: 'mdi-account-heart' });

  return items;
});

// Allow deep-linking a tab via ?tab= (no client router, so read the query string
// directly). Unknown or unauthorized tabs fall back to general.
function initialTab(): string {
  const requested = new URLSearchParams(window.location.search).get('tab');

  return requested && tabItems.value.some((item) => item.value === requested) ?
    requested : 'general';
}

const tab = ref(initialTab());
</script>

<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <v-card class="mb-4 pa-4">
          <div class="d-flex align-center flex-wrap ga-2">
            <span class="text-h6 font-weight-bold">Filtros</span>
            <div
              class="d-flex align-center flex-wrap flex-md-nowrap ga-2 ml-md-auto"
              data-testid="filter-controls"
            >
              <group-filter-select
                v-model="groupIds"
                variant="outlined"
                density="compact"
                hide-details
                class="dashboard-filter"
              />
              <service-category-filter-select
                v-model="categoryIds"
                v-model:uncategorized="uncategorized"
                variant="outlined"
                density="compact"
                hide-details
                class="dashboard-filter"
              />
              <career-filter-select
                v-model="careerFilter"
                :offset="filters.offset"
                variant="outlined"
                density="compact"
                hide-details
                class="dashboard-filter"
              />
              <div
                class="d-flex align-center flex-wrap ga-1 flex-shrink-0"
                data-testid="month-controls"
              >
                <v-btn
                  variant="outlined"
                  size="small"
                  @click="resetOffset()"
                >
                  Mes Actual
                </v-btn>
                <v-btn
                  icon="mdi-chevron-left"
                  size="small"
                  density="comfortable"
                  variant="text"
                  @click="updateOffset(-1)"
                />
                <p class="mx-2">
                  {{ currentDateText }}
                </p>
                <v-btn
                  icon="mdi-chevron-right"
                  size="small"
                  density="comfortable"
                  variant="text"
                  @click="updateOffset(1)"
                />
                <v-menu
                  v-model="monthPickerOpen"
                  :close-on-content-click="false"
                >
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      icon="mdi-calendar"
                      size="small"
                      density="comfortable"
                      variant="text"
                    />
                  </template>
                  <!-- The months grid on its own, rather than the full date picker: it is
                       the only part this filter needs, and unlike the picker it reports
                       every click — including re-picking the highlighted month. -->
                  <v-card class="pa-2">
                    <div class="d-flex align-center justify-space-between">
                      <v-btn
                        icon="mdi-chevron-left"
                        variant="text"
                        density="comfortable"
                        size="small"
                        :aria-label="`Año ${pickerYear - 1}`"
                        @click="pickerYear -= 1"
                      />
                      <span class="text-subtitle-2">{{ pickerYear }}</span>
                      <v-btn
                        icon="mdi-chevron-right"
                        variant="text"
                        density="comfortable"
                        size="small"
                        :aria-label="`Año ${pickerYear + 1}`"
                        @click="pickerYear += 1"
                      />
                    </div>
                    <v-date-picker-months
                      :model-value="pickerMonth"
                      :year="pickerYear"
                      @update:model-value="selectMonth"
                    />
                  </v-card>
                </v-menu>
              </div>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <responsive-tabs
          v-model="tab"
          :items="tabItems"
        />
        <!-- pa-1 keeps card shadows inside the v-window's overflow clip -->
        <v-window
          v-model="tab"
          class="mt-4 pa-1"
        >
          <v-window-item value="general">
            <monthly-summaries-show
              :query-params="queryParams"
              :selected-month-text="currentDateText"
            />
          </v-window-item>
          <v-window-item
            v-if="canReadFinancialInformation"
            value="finances"
          >
            <financial-summaries-show
              :query-params="queryParams"
              :selected-month-text="currentDateText"
            />
          </v-window-item>
          <v-window-item value="patients">
            <patient-metrics-tab
              :query-params="queryParams"
              :selected-month-text="currentDateText"
            />
          </v-window-item>
        </v-window>
      </v-col>
    </v-row>
  </v-container>
</template>

<style scoped>
.dashboard-filter {
  flex: 0 1 240px;
  min-width: 200px;
}

@media (max-width: 600px) {
  .dashboard-filter {
    flex-basis: 100%;
    min-width: 100%;
  }
}
</style>
